import { Link } from "react-router-dom";
import moment from "moment";

import AdminBadge from "../../_components/AdminBadge";
import { ArrowOutwardIcon } from "../../../icons";
import { cn } from "../../../../utils/classnames";
import { EFFECTIVE_STATUS_LABELS, getBookingDetailPath } from "../constants";

/**
 * One booking as the calendar shows it in full: when, who, where, and — the
 * reason this page exists — which vehicle and driver each of its slots got.
 *
 * Used by both the day panel and the list view, so it carries its own card
 * chrome and stays independent of where it is rendered.
 */

// Keyed by the effective status from `getEffectiveBookingStatus`, not by the raw
// `bookingStatus` — see the booking detail page for the same three states.
export const EFFECTIVE_STATUS_TONES = {
  CONFIRMED: "success",
  PENDING: "warning",
  CANCELLED: "danger",
};

export const getBookingTypeLabel = (bookingType, isAirportTransfer) => {
  if (bookingType === "byTheHour") return "By The Hour";
  if (isAirportTransfer) return "Airport Transfer";
  return "Specified Destination";
};

const Field = ({ label, children }) => (
  <div>
    <div className="tw:text-[12px] tw:leading-[18px] tw:!text-[#8D8D8D]">{label}</div>
    <div className="tw:text-[14px] tw:leading-[20px] tw:!text-[#282828]">{children}</div>
  </div>
);

/** One vehicle slot: the assignment, or a plain statement that there isn't one. */
const SlotRow = ({ slot, index, showIndex }) => (
  <div
    className={cn(
      "tw:rounded-[5px] tw:border tw:px-3 tw:py-[10px]",
      slot.isAssigned
        ? "tw:border-[#E6E6E6] tw:bg-[#FAFAFA]"
        : "tw:border-[#F6E2B8] tw:bg-[#FFFAF0]",
    )}
  >
    <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
      <span className="tw:text-[12px] tw:font-semibold tw:uppercase tw:tracking-[0.5px] tw:!text-[#8D8D8D]">
        {showIndex ? `Vehicle ${index + 1}` : "Vehicle"}
        {slot.vehicleTypeName && slot.vehicleTypeName !== "-" ? ` · ${slot.vehicleTypeName}` : ""}
      </span>
      {slot.isAssigned ? null : (
        <span className="tw:text-[12px] tw:font-semibold tw:!text-[#A3780F]">Not assigned</span>
      )}
    </div>

    <div className="tw:mt-2 tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-2">
      <Field label="Vehicle">
        {slot.vehicleName && slot.vehicleName !== "-" ? (
          <>
            {slot.vehicleName}
            {slot.registrationNumber && slot.registrationNumber !== "-" ? (
              <span className="tw:!text-[#8D8D8D]"> · {slot.registrationNumber}</span>
            ) : null}
          </>
        ) : (
          <span className="tw:!text-[#A3780F]">—</span>
        )}
      </Field>
      <Field label="Driver">
        {slot.driverName && slot.driverName !== "-" ? (
          <>
            {slot.driverName}
            {slot.driverPhone && slot.driverPhone !== "-" ? (
              <span className="tw:!text-[#8D8D8D]"> · {slot.driverPhone}</span>
            ) : null}
          </>
        ) : (
          <span className="tw:!text-[#A3780F]">—</span>
        )}
      </Field>
    </div>
  </div>
);

const CalendarBookingCard = ({ booking, showDate = false }) => {
  const pickup = booking.pickupDateTime ? moment(booking.pickupDateTime).locale("en") : null;
  const dropoff = booking.dropoffDateTime ? moment(booking.dropoffDateTime).locale("en") : null;
  const unassignedCount = booking.slots.filter((slot) => !slot.isAssigned).length;

  return (
    <article className="tw:rounded-[5px] tw:border tw:border-[#E6E6E6] tw:bg-white tw:p-4">
      <div className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-2">
        <div>
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            <span className="tw:text-[16px] tw:font-semibold tw:!text-[#282828]">
              {pickup ? pickup.format("H:mm") : "--:--"}
              {dropoff ? <span className="tw:!text-[#8D8D8D]"> – {dropoff.format("H:mm")}</span> : null}
            </span>
            {showDate && pickup ? (
              <span className="tw:text-[13px] tw:!text-[#8D8D8D]">{pickup.format("YYYY/MM/DD (ddd)")}</span>
            ) : null}
          </div>
          <div className="tw:mt-1 tw:text-[15px] tw:!text-[#282828]">{booking.customerName}</div>
        </div>

        <div className="tw:flex tw:items-center tw:gap-2">
          <span className="tw:w-[170px]">
            <AdminBadge tone={EFFECTIVE_STATUS_TONES[booking.effectiveStatus] || "neutral"}>
              {EFFECTIVE_STATUS_LABELS[booking.effectiveStatus] || "-"}
            </AdminBadge>
          </span>
          {/* New tab: the calendar is a working view an admin keeps open while
              opening bookings out of it. */}
          <Link
            to={getBookingDetailPath(booking)}
            target="_blank"
            rel="noopener noreferrer"
            className="tw:flex tw:h-[40px] tw:items-center tw:gap-[6px] tw:rounded-[5px] tw:border tw:border-[#282828] tw:px-3 tw:text-[14px] tw:!text-[#282828] tw:!no-underline"
          >
            Open
            <ArrowOutwardIcon color="#282828" size={16} />
          </Link>
        </div>
      </div>

      <div className="tw:mt-3 tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-2">
        <Field label="Booking ID">{`#${booking.key || "-"}`}</Field>
        <Field label="Transfer Type">
          {getBookingTypeLabel(booking.bookingType, booking.isAirportTransfer)}
        </Field>
        <Field label="Pick-up">{booking.pickupLocation || "-"}</Field>
        <Field label="Drop-off">{booking.destination || "-"}</Field>
      </div>

      <div className="tw:mt-3 tw:flex tw:flex-col tw:gap-2">
        {booking.slots.length ? (
          booking.slots.map((slot, index) => (
            <SlotRow
              key={`${booking._id}-${slot.vehicleSlot}`}
              slot={slot}
              index={index}
              showIndex={booking.slots.length > 1}
            />
          ))
        ) : (
          <div className="tw:rounded-[5px] tw:border tw:border-[#F6E2B8] tw:bg-[#FFFAF0] tw:px-3 tw:py-[10px] tw:text-[13px] tw:!text-[#A3780F]">
            This booking has no vehicles on it yet.
          </div>
        )}
      </div>

      {unassignedCount ? (
        <div className="tw:mt-3 tw:text-[13px] tw:!text-[#A3780F]">
          {unassignedCount} of {booking.slots.length} vehicle{booking.slots.length === 1 ? "" : "s"} still
          needs a driver and a car.
        </div>
      ) : null}
    </article>
  );
};

export default CalendarBookingCard;
